/// <reference types="@types/apple-mapkit-js-browser" />
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Box } from '@rbilabs/universal-components';
import { defaultFocusZoom, defaultInitialZoom } from 'features/store/constants';
import { debounce } from 'lodash-es';

import { useLocale } from 'state/intl';
import { primitive } from 'styles/constants';
import { hasLoadedMapKitLibraries, loadMapKitJSLibrary } from 'utils/apple-mapkit/index.web';
import { getConfigValue } from 'utils/environment';
import { isValidPosition } from 'utils/geolocation';
import noop from 'utils/noop';

import { MARKER_URIS } from '../use-map/icons/index.web';
import { checkSingleUseMarker, getKeyForMarkerInput } from '../use-map/marker-utils';

import mapDefaults from './defaults';
import DevTokenInfo from './dev-token-info.web';
import {
  CenterPointExtended,
  IMarker,
  IMarkerInput,
  IUseMapExtendedParam,
  IUseMapProvider,
  MarkerTypes,
  PanToFn,
} from './types';

const MapContainer = Box.withConfig({
  backgroundColor: primitive.$mayoEggWhite,
  flexGrow: 1,
  flexShrink: 1,
  flexBasis: 'auto',
  height: '100%',
  width: 'full',
});

/**
 * Convert Google Maps zoom level to MapKit CoordinateSpan
 * Formula: span = 360 / (2^zoom)
 *
 * Apple MapKit interprets spans differently than Google Maps, requiring a correction factor
 * to match the visual zoom level. We use a factor of ~1.8x to make Apple Maps show similar
 * area coverage as Google Maps at the same zoom level.
 */
function zoomToSpan(zoom: number): { latitudeDelta: number; longitudeDelta: number } {
  const baseDegrees = 360 / Math.pow(2, zoom);
  // Apply correction factor to match Google Maps visual zoom level
  // This makes Apple Maps show a similar area as Google Maps at the same zoom level
  const correctionFactor = 1.8;
  const degrees = baseDegrees * correctionFactor;
  return {
    latitudeDelta: degrees,
    longitudeDelta: degrees,
  };
}

/**
 * Default/empty result returned when hook is disabled
 */
const DEFAULT_RESULT = {
  actualMapCenterLatLng: { lat: 0, lng: 0, zoom: defaultInitialZoom },
  mapComponent: null,
  focusToPoint: noop as PanToFn,
  focusToPoints: noop,
  renderMarkers: noop,
  createMarker: () => '',
  createMarkerList: noop,
  getCurrentCenter: () => ({ lat: 0, lng: 0, zoom: defaultInitialZoom }),
};

const useAppleMap: IUseMapProvider = (props: IUseMapExtendedParam, enabled = true) => {
  const { startPosition, disableControls = false, onDragEnd } = props;
  const [map, setMap] = useState<mapkit.Map | null>(null);
  const [libraryLoaded, setLibraryLoaded] = useState(hasLoadedMapKitLibraries);
  const markers = useRef<Record<string, IMarkerInput & { annotation: mapkit.ImageAnnotation }>>({});
  const selectedMarkerKey = useRef<string | undefined>(undefined);
  const [isMapMounted, setIsMapMounted] = useState(false);
  const domRef = useRef<HTMLDivElement | null>(null);
  const onDragEndRef = useRef(onDragEnd);

  // Update ref when onDragEnd changes
  useEffect(() => {
    onDragEndRef.current = onDragEnd;
  }, [onDragEnd]);

  // MapKit Web tokens are pinned to an origin and so may not work locally.
  // If you need to debug, generate a temporary scope-less token at:
  // https://developer.apple.com/account/resources/services/maps-tokens
  const { mapKitWebJSToken } = getConfigValue({ key: 'apple', defaultValue: {} });
  const { region } = useLocale();
  const defaultPosition = mapDefaults[region] ?? mapDefaults.default;
  const startPos = startPosition || defaultPosition;
  const center = useRef<CenterPointExtended>({
    lat: startPos.lat,
    lng: startPos.lng,
    zoom: startPos.zoom || defaultInitialZoom,
  });

  // Load MapKit JS library
  useEffect(() => {
    if (!enabled || libraryLoaded || !mapKitWebJSToken) {
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        await loadMapKitJSLibrary(mapKitWebJSToken);
        if (!cancelled) {
          setLibraryLoaded(true);
        }
      } catch {
        // keep disabled
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, libraryLoaded, mapKitWebJSToken]);

  // Initialize map
  useEffect(() => {
    if (!enabled || !libraryLoaded || !domRef.current) {
      return;
    }

    try {
      const initialZoom = center.current.zoom || defaultInitialZoom;
      const span = zoomToSpan(initialZoom);
      const initialRegion = new globalThis.mapkit.CoordinateRegion(
        new globalThis.mapkit.Coordinate(center.current.lat, center.current.lng),
        new globalThis.mapkit.CoordinateSpan(span.latitudeDelta, span.longitudeDelta)
      );

      const mapInstance = new globalThis.mapkit.Map(domRef.current, {
        region: initialRegion,
        isRotationEnabled: false,
        showsMapTypeControl: false,
        showsCompass: mapkit.FeatureVisibility.Hidden,
        showsZoomControl: false,
        // @ts-expect-error: missing types
        loadPriority: globalThis.mapkit.Map.LoadPriorities.LandCover,
      });

      // Set up event listeners
      const blurActiveElement = () => {
        const activeElement = document.activeElement as HTMLElement | null;
        activeElement?.blur?.();
      };

      const updateCenter = debounce(() => {
        try {
          const mapCenter = mapInstance.center;
          center.current = {
            lat: mapCenter.latitude,
            lng: mapCenter.longitude,
            zoom: center.current.zoom || defaultInitialZoom,
          };
        } catch {
          // Map may have been destroyed, ignore error
        }
      }, 500);

      // Combined handler for region-change-end (updates center and calls onDragEnd)
      const handleRegionChangeEnd = () => {
        updateCenter();
        onDragEndRef.current();
      };

      mapInstance.addEventListener('scroll-start', blurActiveElement);
      mapInstance.addEventListener('region-change-end', handleRegionChangeEnd);

      setMap(mapInstance);

      // Set initial gesture controls
      try {
        mapInstance.isScrollEnabled = !disableControls;
        mapInstance.isZoomEnabled = !disableControls;
      } catch {
        // Map may not be ready yet, ignore error
      }

      return () => {
        try {
          // Clean up event listeners before destroying the map
          mapInstance.removeEventListener('scroll-start', blurActiveElement);
          mapInstance.removeEventListener('region-change-end', handleRegionChangeEnd);
          mapInstance.destroy();
        } catch {
          // Map may have already been destroyed, ignore error
        }
      };
    } catch (error) {
      // If map initialization fails, don't break the app
      // Don't set map, so mapComponent will remain null and no map will be displayed
    }
  }, [enabled, libraryLoaded, isMapMounted]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle gesture controls updates
  useEffect(() => {
    if (!map) {
      return;
    }
    try {
      map.isScrollEnabled = !disableControls;
      map.isZoomEnabled = !disableControls;
    } catch {
      // Map may have been destroyed, ignore error
    }
  }, [map, disableControls]);

  const onLoadedIntoDom = useCallback((ref: HTMLDivElement) => {
    if (!domRef.current && ref) {
      domRef.current = ref;
      setIsMapMounted(true);
    }
  }, []);

  const getCurrentCenter = useCallback(() => {
    return center.current;
  }, []);

  const focusToPoint = useCallback<PanToFn>(
    newPosition => {
      if (!libraryLoaded || !map || !isValidPosition(newPosition)) {
        return;
      }

      try {
        // Verify map is fully initialized by checking if it has a region
        // If the map is not ready, setRegionAnimated will fail
        if (!map.region) {
          return;
        }

        const coordinate = new globalThis.mapkit.Coordinate(newPosition.lat!, newPosition.lng!);
        const zoom = Math.max(defaultFocusZoom, center.current.zoom || defaultFocusZoom);

        const spanValue = zoomToSpan(zoom);
        const span = new globalThis.mapkit.CoordinateSpan(
          spanValue.latitudeDelta,
          spanValue.longitudeDelta
        );
        const region = new globalThis.mapkit.CoordinateRegion(coordinate, span);
        map.setRegionAnimated(region, true);

        center.current = { lat: newPosition.lat!, lng: newPosition.lng!, zoom };
      } catch {
        // Map may not be fully initialized yet, silently fail
      }
    },
    [libraryLoaded, map]
  );

  const focusToPoints = useCallback(
    (points: any[]) => {
      if (!libraryLoaded || !map || points.length === 0) {
        return;
      }

      try {
        // Verify map is fully initialized by checking if it has a region
        // If the map is not ready, setRegionAnimated will fail
        if (!map.region) {
          return;
        }

        const coordinates = points.map(
          point =>
            new globalThis.mapkit.Coordinate(
              point.lat || point.latitude,
              point.lng || point.longitude
            )
        );

        const maxLat = Math.max(...coordinates.map(c => c.latitude));
        const maxLng = Math.max(...coordinates.map(c => c.longitude));
        const minLat = Math.min(...coordinates.map(c => c.latitude));
        const minLng = Math.min(...coordinates.map(c => c.longitude));

        // Use showItems with pixel-based padding (like Google's fitBounds)
        const annotations = map.annotations;
        if (annotations && annotations.length > 0) {
          // Calculate padding proportional to container size for responsive behavior
          const mapRect = (map.element as HTMLElement)?.getBoundingClientRect?.();

          // Padding as percentage of container (10% vertical, 5% horizontal)
          // Falls back to minimum values if container dimensions unavailable
          const verticalPadding = mapRect?.height ? Math.max(20, mapRect.height * 0.1) : 20;
          const horizontalPadding = mapRect?.width ? Math.max(15, mapRect.width * 0.05) : 15;

          const padding = new globalThis.mapkit.Padding(
            verticalPadding,
            horizontalPadding,
            verticalPadding,
            horizontalPadding
          );
          map.showItems(annotations, { animate: true, padding });
        } else {
          // Fallback when no annotations: use coordinate-based region
          const latSpan = maxLat - minLat;
          const lngSpan = maxLng - minLng;
          const expandFactor = 0.4;
          const minPadding = 0.01;

          const latPadding = Math.max(latSpan * expandFactor, minPadding);
          const lngPadding = Math.max(lngSpan * expandFactor, minPadding);

          const expandedBoundingRegion = new globalThis.mapkit.BoundingRegion(
            maxLat + latPadding,
            maxLng + lngPadding,
            minLat - latPadding,
            minLng - lngPadding
          );
          map.setRegionAnimated(expandedBoundingRegion.toCoordinateRegion(), true);
        }
      } catch {
        // Map may not be fully initialized yet, silently fail
      }
    },
    [libraryLoaded, map]
  );

  const createMarker = useCallback(
    (markerInput: IMarker): string => {
      if (!libraryLoaded || !map) {
        return '';
      }

      // Verify map is fully initialized before adding annotations
      try {
        if (!map.region) {
          return '';
        }
      } catch {
        // Map may have been destroyed
        return '';
      }

      const { type, location, onPress, isSelected, icon, title } = markerInput;
      const key = getKeyForMarkerInput(markerInput, checkSingleUseMarker(type));

      if (!isValidPosition(location)) {
        return key; // bail if location is not known
      }

      selectedMarkerKey.current = isSelected ? key : selectedMarkerKey.current;

      // Use icon from markerInput if provided, otherwise use MARKER_URIS
      const iconUrl =
        (icon as string) ||
        (isSelected ? MARKER_URIS[MarkerTypes.StoreOpenSelected] : MARKER_URIS[type]);

      try {
        const existingMarker = markers.current[key];
        if (existingMarker) {
          // Update existing marker
          existingMarker.annotation.coordinate = new globalThis.mapkit.Coordinate(
            location.lat,
            location.lng
          );

          // Update icon if needed
          if (
            markers.current[key].type !== type ||
            markers.current[key].isSelected !== isSelected
          ) {
            markers.current[key].type = type;
            markers.current[key].isSelected = isSelected;
            // Need to recreate annotation for icon change in MapKit
            map.removeAnnotation(existingMarker.annotation);
            const newAnnotation = new globalThis.mapkit.ImageAnnotation(
              new globalThis.mapkit.Coordinate(location.lat, location.lng),
              { url: { 1: iconUrl } }
            );
            newAnnotation.title = title || '';
            newAnnotation.enabled = !!onPress;
            if (onPress) {
              newAnnotation.addEventListener('select', onPress);
            }
            map.addAnnotation(newAnnotation);
            markers.current[key].annotation = newAnnotation;
          }

          return key;
        }

        // Create new marker
        const annotation = new globalThis.mapkit.ImageAnnotation(
          new globalThis.mapkit.Coordinate(location.lat, location.lng),
          { url: { 1: iconUrl } }
        );

        annotation.title = title || '';
        annotation.enabled = !!onPress;

        if (type === MarkerTypes.UserManual) {
          annotation.visible = false;
        }

        if (onPress) {
          annotation.addEventListener('select', onPress);
        }

        map.addAnnotation(annotation);
        markers.current[key] = { id: key, annotation, type, location, isSelected, onPress };

        return key;
      } catch {
        // Map may have been destroyed or not ready, silently fail
        return '';
      }
    },
    [libraryLoaded, map]
  );

  const createMarkerList = useCallback(
    (markerList: IMarker[]) => {
      const existingMarkersMap = new Map(
        Object.entries(markers.current).map(([key, value]) => [key, value])
      );

      const newMarkersMap = new Map(
        markerList.map(marker => [
          getKeyForMarkerInput(marker, checkSingleUseMarker(marker.type)),
          marker,
        ])
      );

      existingMarkersMap.forEach((value, key) => {
        if (!newMarkersMap.has(key)) {
          try {
            map?.removeAnnotation(value.annotation);
          } catch {
            // Ignore errors during cleanup
          }
          delete markers.current[key];
        }
      });

      markerList.forEach(markerInput => {
        const key = getKeyForMarkerInput(markerInput, checkSingleUseMarker(markerInput.type));
        const existingMarker = existingMarkersMap.get(key);

        if (existingMarker) {
          const needsUpdate =
            existingMarker.type !== markerInput.type ||
            existingMarker.isSelected !== markerInput.isSelected ||
            existingMarker.location.lat !== markerInput.location.lat ||
            existingMarker.location.lng !== markerInput.location.lng;

          if (needsUpdate) {
            createMarker(markerInput);
          }
        } else {
          createMarker(markerInput);
        }
      });
    },
    [createMarker, map]
  );

  // Return default result when disabled
  if (!enabled) {
    return DEFAULT_RESULT;
  }

  const mapElement = (
    <Box flex={1} flexDirection="column" height="100%">
      {__DEV__ && <DevTokenInfo />}
      <MapContainer ref={onLoadedIntoDom} />
    </Box>
  );

  return {
    actualMapCenterLatLng: center.current,
    mapComponent: mapElement,
    focusToPoint,
    focusToPoints,
    renderMarkers: noop,
    createMarker,
    createMarkerList,
    getCurrentCenter,
  };
};

export default useAppleMap;
